<script setup lang="ts">
import { useFormModel } from "@/hooks";
import type { InputAlign } from "@nutui/nutui";
import type {
  CheckboxShape,
  CheckboxTextPosition,
} from "@nutui/nutui/dist/types/__VUE/checkbox/types";
import { computed, ref } from "vue";

interface Option {
  value: string;
  text: string;
  [x: string]: string | boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    placeholder?: string;
    inputAlign?: InputAlign;
    items?: Option[];
    max: number;
    separator?: string;
    optionTextPosition?: CheckboxTextPosition;
    optionIconSize?: string;
    optionShape?: CheckboxShape;
    title?: string;
    cancelText?: string;
    okText?: string;
  }>(),
  {
    items: () => [],
  }
);

const emit = defineEmits(["update:modelValue", "change"]);

const show = ref(false);
const selected = ref<string[]>([]);

const values = computed(() =>
  props.modelValue ? props.modelValue.split(",") : []
);

const text = computed(() =>
  values.value
    .map((val) => props.items.find((item) => item.value === val)?.text)
    .join(props.separator || "")
);

function onClick() {
  show.value = true;
  selected.value = values.value;
}

function cancel() {
  show.value = false;
  selected.value = values.value;
}
function confirm() {
  show.value = false;
  const val = selected.value.join(",");
  emit("update:modelValue", val);
  emit("change", val);
}

const { setFormModel } = useFormModel();
defineExpose({ setFormModel });
</script>
<template>
  <div class="mp-select-multi-enum">
    <nut-input
      :model-value="text"
      :placeholder="placeholder"
      :input-align="inputAlign"
      :border="false"
      readonly
      type="text"
      @click="onClick"
    />
    <nut-popup v-model:visible="show" position="bottom">
      <div class="mp-select-multi-enum__buttons">
        <div class="mp-select-multi-enum__button" @click.stop="cancel">
          {{ cancelText }}
        </div>
        <div class="mp-select-multi-enum__title">{{ title }}</div>
        <div class="mp-select-multi-enum__button" @click.stop="confirm">
          {{ okText }}
        </div>
      </div>
      <nut-checkbox-group
        class="mp-select-multi-enum__select"
        v-model="selected"
        :max="max"
      >
        <nut-checkbox
          class="mp-select-multi-enum__option"
          :label="opt.value"
          :text-position="optionTextPosition"
          :icon-size="optionIconSize"
          :shape="optionShape"
          v-for="opt in items"
        >
          {{ opt.text }}
        </nut-checkbox>
      </nut-checkbox-group>
    </nut-popup>
  </div>
</template>
<style lang="scss">
.mp-select-multi-enum {
  width: 100%;

  .nut-popup {
    border-radius: 10px 10px 0 0;
  }

  &__buttons {
    display: flex;
    justify-content: space-between;
    padding: 15px;
    border-bottom: 1px solid #dcdfe6;
  }

  &__button {
    color: #409eff;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #000;
  }

  &__select {
    display: flex;
    flex-direction: column;
    padding: 5px 15px;
    max-height: 290px;
    overflow-y: auto;
  }

  &__option {
    position: relative;
    height: 45px;
    line-height: 45px;
    margin: 0;

    &::after {
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1px;
      background-color: #dcdfe6;
      content: "";
    }
  }
}
</style>
